<template>
    <div>
        <head-banner :title="product.name" />
        <div class="b_single_product">
            <div class="container">
                <div class="buttons flex">
                    <div class="btn_block">
                        <nuxt-link
                            :to="`/shops/${product.shop.slug}`"
                            class="btn_link second_font"
                        >
                            {{ product.shop.name }}
                        </nuxt-link>
                    </div>
                    <div class="btn_block">
                        <nuxt-link
                            to="/checkout/order"
                            tag="button"
                            class="btn btn_template btn_basket"
                            :disabled="isCartEmpty"
                        >
                            לסל
                        </nuxt-link>
                    </div>
                </div>
                <div class="product_wrapper">
                    <div class="product_block flex">
                        <div class="product_description">
                            <div class="product_title">
                                {{ product.name }}
                            </div>
                            <div class="description">
                                <p>
                                    {{ product.description }}
                                </p>
                            </div>
                            <div class="product_prices">
                                <div class="price">
                                    <span>מחיר לסטודנט:</span>
                                    <span>{{ price | formatPrice }}</span>
                                </div>
                                <div class="price_member">
                                    <span>לחברי האגודה:</span>
                                    <span>{{ agudaPrice | formatPrice }}</span>
                                </div>
                            </div>
                            <div
                                v-if="!loggedUser"
                                class="not_registered cursor_default"
                            >
                                <p>
                                    חברי אגודה משלמים פחות - להרשמה או תשלום
                                    בכוכבים
                                    <a
                                        class="link"
                                        @click="$openModal('login')"
                                    >
                                        <b>הירשמו לאתר</b>
                                    </a>
                                </p>
                            </div>
                            <variant-selector
                                :key="selectorsKey"
                                :variants="product.variants"
                                @variant-changed="onVariantChanged"
                            />

                            <div
                                v-if="!isVariantAvailable"
                                class="not_available"
                            >
                                <div class="error_message">
                                    *המוצר לא נמצא במלאי
                                </div>

                                <button
                                    class="btn btn_template"
                                    type="button"
                                    @click="remindMe"
                                >
                                    עדכנו אותי כשיוחזר
                                </button>
                            </div>

                            <div class="product_controls flex">
                                <div class="product_quantity">
                                    <div class="input_count">
                                        <button
                                            class="btn_plus jsPlus"
                                            type="button"
                                            @click="incrementQuantity"
                                        />
                                        <input
                                            v-model="quantity"
                                            class="input_value"
                                            type="number"
                                            minimum="0"
                                        />
                                        <button
                                            class="btn_minus jsMinus"
                                            type="button"
                                            :disabled="quantity <= 1"
                                            @click="decrementQuantity"
                                        />
                                    </div>
                                </div>
                                <button
                                    class="btn btn_template"
                                    type="button"
                                    :disabled="
                                        !selectedVariant || !isVariantAvailable
                                    "
                                    @click="addToCart"
                                >
                                    הוספה לסל
                                </button>
                            </div>
                        </div>

                        <gallery
                            :key="selectedVariant ? selectedVariant.id : 'none'"
                            :images="gallery"
                        />
                    </div>
                </div>
            </div>
        </div>
        <different-shop-modal redirect-to-current-shop />
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { StatusCodes } from 'http-status-codes';
import headMixin from '@/mixins/head.mixin.js';
import Gallery from '@/components/products/Gallery';
import HeadBanner from '@/components/elements/HeadBanner';
import VariantSelector from '@/components/variants/Selector';
import DifferentShopModal from '@/components/modals/DifferentShop';

export default {
    components: {
        Gallery,
        HeadBanner,
        VariantSelector,
        DifferentShopModal
    },

    mixins: [headMixin],

    async asyncData({
        params: { slug },
        store: { dispatch },
        error,
        redirect,
        route
    }) {
        try {
            const product = await dispatch('products/show', slug);

            return {
                product,
                meta: product.meta
            };
        } catch (err) {
            redirect({
                path: '/?modal=join',
                query: { redirect: route.path }
            });

            if (
                err.response &&
                err.response.status !== StatusCodes.INTERNAL_SERVER_ERROR
            ) {
                return error({ statusCode: err.response.status });
            }

            console.error(err);

            return error({ statusCode: StatusCodes.INTERNAL_SERVER_ERROR });
        }
    },

    data() {
        return {
            selectedVariant: null,
            quantity: 1,
            selectorsKey: 0,
            stockAmount: null
        };
    },

    computed: {
        ...mapGetters({
            items: 'cart/items',
            currentShopId: 'cart/currentShopId',
            loggedUser: 'loggedUser'
        }),

        isCartEmpty() {
            return !this.items.length;
        },

        isVariantAvailable() {
            if (!this.selectedVariant) {
                return true;
            }

            const variant = this.items.find(
                item => item.id === this.selectedVariant.id
            );

            const cartQuantity = variant?.quantity || 0;

            if (this.stockAmount !== null) {
                return this.stockAmount - this.quantity - cartQuantity >= 0;
            }

            return true;
        },

        price() {
            return this.selectedVariant?.price || this.product.price;
        },

        agudaPrice() {
            return (
                this.selectedVariant?.agudaPrice ||
                this.selectedVariant?.price ||
                this.product.agudaPrice ||
                this.product.price
            );
        },

        gallery() {
            if (this.selectedVariant?.gallery?.length) {
                return this.selectedVariant.gallery;
            }

            return this.product.featuredImage
                ? [this.product.featuredImage, ...this.product.gallery]
                : this.product.gallery;
        }
    },

    watch: {
        async quantity(val) {
            if (val < 1) {
                this.quantity = 1;
            }

            if (this.selectedVariant?.id) {
                const { stockAmount } = await this.show(
                    this.selectedVariant.id
                );

                this.stockAmount = stockAmount;
            }
        },

        selectedVariant: {
            async handler() {
                if (this.selectedVariant?.id) {
                    const { stockAmount } = await this.show(
                        this.selectedVariant.id
                    );

                    this.stockAmount = stockAmount;
                }
            },
            deep: true
        }
    },

    methods: {
        ...mapActions({
            show: 'productVariants/show',
            addItemToCart: 'cart/addItem',
            remind: 'productVariants/remind',
            setCurrentShopId: 'cart/setCurrentShopId',
            setCanPayWithStars: 'cart/setCanPayWithStars'
        }),

        onVariantChanged(variant) {
            this.selectedVariant = variant;
        },

        addToCart() {
            const variant = {
                ...this.selectedVariant,
                product: this.product,
                quantity: parseInt(this.quantity)
            };

            const { id: shopId, payWithStars } = variant.product.shop;

            if (!this.currentShopId) {
                this.setCurrentShopId(shopId);
                this.setCanPayWithStars(payWithStars);
            }

            if (this.currentShopId !== shopId) {
                return this.$openModal('differentShop');
            }

            delete variant.product.varaints;

            this.addItemToCart(variant);
            this.quantity = 1;
            this.selectorsKey++;
            this.selectedVariant = null;

            this.$toasted.success('הפריט הוסף בהצלחה!');
        },

        incrementQuantity() {
            this.quantity++;
        },

        decrementQuantity() {
            this.quantity--;
        },

        async remindMe() {
            if (!this.loggedUser) {
                return this.$openModal('login');
            }

            try {
                await this.remind(this.selectedVariant.id);

                this.$toasted.success(
                    'קיבלנו, נודיע לך בדו"אל כשהפריט יוחזר למלאי.'
                );
            } catch (error) {
                console.error(error);

                this.$toasted.global.error();
            }
        }
    }
};
</script>
